/**
 * Infinite scroll for collection PLPs.
 *
 * Replaces paginated navigation with lazy-loading of the next set of products.
 * The traditional <page-pagination> element stays in the HTML for SEO but is
 * hidden visually once this component activates.
 *
 * When filters or sort options change, ProductFacet._rerender() replaces the
 * entire #facet-main block, which destroys and re-creates this element —
 * automatically resetting infinite scroll back to page 1.
 */

class InfiniteScrollTrigger extends HTMLElement {
  connectedCallback() {
    this._loading = false;
    this._nextUrl = this.getAttribute('data-next-url');
    this._sectionId = this.getAttribute('data-section-id');

    // Hide the traditional pagination controls.
    // They remain in the HTML source for SEO crawlability.
    const pagination = this.closest('#facet-main')?.querySelector('.pagination');
    if (pagination) pagination.hidden = true;

    // Nothing to do if there is no next page.
    if (!this._nextUrl) return;

    this._insertLoader();

    this._observer = new IntersectionObserver(
      this._onIntersect.bind(this),
      // Start fetching 400px before the trigger enters the viewport so
      // products appear before the user actually reaches the bottom.
      { rootMargin: '400px 0px' }
    );
    this._observer.observe(this);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  _insertLoader() {
    this.insertAdjacentHTML(
      'beforeend',
      `<div class="infinite-scroll-trigger__loader" aria-hidden="true" hidden>
        <span class="infinite-scroll-trigger__dot"></span>
        <span class="infinite-scroll-trigger__dot"></span>
        <span class="infinite-scroll-trigger__dot"></span>
      </div>`
    );
    this._loader = this.querySelector('.infinite-scroll-trigger__loader');
  }

  _onIntersect(entries) {
    if (entries[0].isIntersecting && !this._loading) {
      this._loadNext();
    }
  }

  async _loadNext() {
    if (this._loading || !this._nextUrl) return;

    this._loading = true;
    this._loader.hidden = false;

    try {
      // Re-use the same section-rendering trick that ProductFacet uses:
      // appending ?section_id= returns only this section's HTML, not the
      // full page layout — much smaller payload.
      const fetchUrl = new URL(this._nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', this._sectionId);

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Append new product cards into the existing grid.
      const productListInner = this.closest('#facet-main')?.querySelector('.product-list__inner');
      if (productListInner) {
        // Capture the page height now so we can restore the user's visual position
        // immediately after the DOM change — before the browser renders the next frame.
        const triggerAboveViewport = this.getBoundingClientRect().top < 0;
        const prevScrollHeight = triggerAboveViewport ? document.documentElement.scrollHeight : 0;

        doc.querySelectorAll('.product-list__inner > *').forEach(item => {
          // The theme's stagger-apparition feature marks items with a `reveal`
          // attribute and uses an IntersectionObserver (set up once on page load)
          // to animate them into view. Newly appended items are never observed,
          // so they stay invisible. Removing the attribute makes them visible
          // immediately, which is correct — they're already being lazy-loaded.
          item.removeAttribute('reveal');
          productListInner.appendChild(document.adoptNode(item));
        });

        if (triggerAboveViewport) {
          const heightAdded = document.documentElement.scrollHeight - prevScrollHeight;
          if (heightAdded > 0) {
            // Scroll down by the same amount the page grew so content below the
            // product list stays in exactly the same visual position.
            window.scrollBy(0, heightAdded);
          }
        }
      }

      // Pull the next-page URL from the fetched page's trigger element.
      const nextTrigger = doc.querySelector('infinite-scroll-trigger');
      this._nextUrl = nextTrigger?.getAttribute('data-next-url') ?? null;

      if (!this._nextUrl) {
        // All pages loaded — remove the trigger and stop observing.
        this._observer.disconnect();
        this.remove();
        return;
      }
    } catch (err) {
      console.error('[InfiniteScroll] Failed to load next page:', err);
    } finally {
      // Always reset loading state so the spinner can never get permanently stuck.
      this._loading = false;
      this._loader.hidden = true;
    }

    // After a successful load, the IntersectionObserver won't re-fire on its own
    // if the trigger element is still within the observed zone (e.g. when the user
    // scrolled fast through multiple pages). Re-check visibility manually and
    // immediately queue the next load if needed.
    if (this._nextUrl) {
      const rect = this.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 400) {
        this._loadNext();
      }
    }
  }
}

if (!customElements.get('infinite-scroll-trigger')) {
  customElements.define('infinite-scroll-trigger', InfiniteScrollTrigger);
}
